// src/features/developer/components/JwtViewer.tsx
import React, { useEffect, useRef } from 'react';
import Editor, { OnChange, loader } from '@monaco-editor/react';
import { useTheme, CircularProgress, Box } from '@mui/material';

// Ensure monaco is loaded
loader.init();

interface JwtViewerProps {
    value: string;
    onChange?: OnChange;
    readOnly?: boolean;
    placeholder?: string;
}

const JwtViewer: React.FC<JwtViewerProps> = ({ value, onChange, readOnly = false, placeholder }) => {
    const theme = useTheme();
    const monacoRef = useRef<any>(null);

    const jwtThemeName = 'jwtTheme';

    useEffect(() => {
        if (monacoRef.current) {
            const monaco = monacoRef.current;
            // Define a custom theme based on the current MUI theme
            monaco.editor.defineTheme(jwtThemeName, {
                base: theme.palette.mode === 'dark' ? 'vs-dark' : 'vs',
                inherit: true,
                rules: [
                    { token: 'jwt-header', foreground: 'd94a6f' }, // A reddish-pink color for header
                    { token: 'jwt-payload', foreground: '6a99e0' }, // A blue color for payload
                    { token: 'jwt-signature', foreground: '4ec9b0' }, // A teal color for signature
                    { token: 'jwt-delimiter', foreground: theme.palette.text.secondary },
                ],
                colors: {
                    'editor.background': theme.palette.background.paper,
                },
            });

            // Define custom tokenization rules for JWT
            monaco.languages.setMonarchTokensProvider('jwt', {
                tokenizer: {
                    root: [
                        // Pattern to match header.payload.signature
                        [/([^.]*)/, 'jwt-header'],
                        [/\./, 'jwt-delimiter'],
                        [/([^.]*)/, 'jwt-payload'],
                        [/\./, 'jwt-delimiter'],
                        [/(.*)/, 'jwt-signature'],
                    ],
                },
            });
        }
    }, [theme.palette.mode, theme.palette.background.paper, theme.palette.text.secondary]);


    const handleEditorDidMount = (editor: any, monaco: any) => {
        monacoRef.current = monaco;
    };

    return (
        <Box sx={{ height: '100%', minHeight: '150px', width: '100%', border: `1px solid ${theme.palette.divider}`, borderRadius: 1, position: 'relative', overflow: 'hidden' }}>
            {!value && placeholder && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: '15px',
                        left: '60px',
                        color: 'text.disabled',
                        zIndex: 1,
                        pointerEvents: 'none',
                    }}
                >
                    {placeholder}
                </Box>
            )}
            <Editor
                height="100%"
                language="jwt" // Use our custom language
                value={value}
                onChange={onChange}
                theme={jwtThemeName} // Use our custom theme
                onMount={handleEditorDidMount}
                loading={<CircularProgress />}
                options={{
                    readOnly,
                    minimap: { enabled: false },
                    scrollBeyondLastLine: false,
                    automaticLayout: true,
                    wordWrap: 'on',
                    contextmenu: true,
                    fontSize: 14,
                    lineNumbers: 'off',
                    glyphMargin: false,
                    folding: false,
                    lineDecorationsWidth: 5,
                    lineNumbersMinChars: 0,
                }}
            />
        </Box>
    );
};

export default JwtViewer;